import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useAsync} from "react-async-hook";
import ReactModal from 'react-modal';
import { useForm } from 'react-hook-form';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import useLoading from '../../../../hooks/useLoading';

import { roomCustomerApi } from '../../../../apis/admin/room/roomCustomerApi';
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {logError} from "../../../../libs/utils/logError";

interface UpdateCustomerModalProps {
  userId: number | undefined;
  onSubmitted: (isChange: boolean) => Promise<void>;
}

export const UpdateCustomerModal = (props: UpdateCustomerModalProps) => {
  const { userId, onSubmitted } = props;

  const withLoading = useLoading();
  const location = useLocation();
  const { success } = useSystemMessage();
  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  useAsync(async () => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await roomCustomerApi.show(userId!, {});

          setValue('userName', response.customer.userName);
          setValue('companyName', response.customer.companyName);
          setValue('zip', response.customer.zip);
          setValue('address', response.customer.address);
          setValue('building', response.customer.building);
          setValue('tel', response.customer.tel);
          setValue('department', response.customer.department);
          setValue('position', response.customer.position);
          setValue('email', response.customer.email);

        } catch(e) {
          await logError(
            location.pathname,
            '顧客ユーザー情報の取得に失敗しました。',
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleUpdate = async () => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: any ) => {
        try {
          await roomCustomerApi.update(
            userId!,
            data,
            {}
          );

          await onSubmitted(true);
          await success(`${data.userName} のユーザ情報を更新しました。`);

        } catch (e) {
          await logError(
            location.pathname,
            '顧客ユーザー情報の更新に失敗しました。',
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted(false)}
        className="form-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <section className="modal-background">
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">顧客情報編集</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted(false)}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit(handleUpdate)} className="">
            <div className="form-modal-content overflow-y-auto">
              <div className="mb-4 text-danger text-xs">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>

              <div className="w-full max-h-96">
                <label className="block" htmlFor="userName">
                  <div className="text-xs font-semibold text-primary mb-1">
                    <span>お名前</span>
                    <span className="ml-2 required">必須</span>
                  </div>
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('userName',
                      makeRegisterOptions('お名前', [
                        { type: FormValidationType.REQUIRED },
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                      ])
                    )}
                    className="text-field"
                    id="userName"
                    type="text"
                    maxLength={100}
                  />
                  <div className="mt-1 text-danger text-xs">
                    {errors.userName?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'userName',
                    }}
                  />
                </label>

                <label className="block mt-4" htmlFor="userName">
                  <div className="text-xs font-semibold text-primary mb-1">
                    <span>メールアドレス</span>
                    <span className="ml-2 required">必須</span>
                  </div>
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('email',
                      makeRegisterOptions('メールアドレス', [
                        { type: FormValidationType.REQUIRED },
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                        { type: FormValidationType.EMAIL, typeName: 'email' },
                      ])
                    )}
                    id="email"
                    type="text"
                    className="text-field"
                    maxLength={86}
                  />
                  <div className="mt-1 text-danger text-xs">
                    {errors.email?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'email',
                    }}
                  />
                </label>

                <label className="block mt-4" htmlFor="companyName">
                  <div className="text-xs font-semibold text-primary mb-1">
                    <span>会社名</span>
                  </div>
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('companyName',
                      makeRegisterOptions('会社名', [
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                      ])
                    )}
                    className="text-field"
                    id="companyName"
                    type="text"
                    maxLength={100}
                  />
                  <div className="mt-1 text-danger text-xs">
                    {errors.companyName?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'companyName',
                    }}
                  />
                </label>

                <label className="block mt-4" htmlFor="department">
                  <div className="text-xs font-semibold text-primary mb-1">
                    <span>部署名</span>
                  </div>
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('department',
                      makeRegisterOptions('部署名', [
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                      ])
                    )}
                    className="text-field"
                    id="department"
                    type="text"
                    maxLength={100}
                  />
                  <div className="mt-1 text-danger text-xs">
                    {errors.department?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'department',
                    }}
                  />
                </label>

                <label className="block mt-4" htmlFor="position">
                  <div className="text-xs font-semibold text-primary mb-1">
                    <span>役職</span>
                  </div>
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register('position',
                      makeRegisterOptions('役職', [
                        { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                      ])
                    )}
                    className="text-field"
                    id="position"
                    type="text"
                    maxLength={100}
                  />
                  <div className="mt-1 text-danger text-xs">
                    {errors.position?.message}
                  </div>
                  <ValidationErrorMessages
                    {...{
                      fieldErrors: apiClientError?.errors,
                      field: 'position',
                    }}
                  />
                </label>
              </div>

            </div>

            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted(false)}
                >
                  キャンセル
                </button>
                <input
                  className="btn-primary"
                  type="button"
                  value="更　新"
                  disabled={isSubmitting}
                  onClick={handleUpdate}
                />
              </div>
            </div>

          </form>
        </div>
        </section>
      </ReactModal>
    </>
  );
}
